/**
 * WhatsNewAutoPopup — the "automatic push".
 *
 * Mount this ONCE per shell (OfficeLayout, TechLayout). The first time a user
 * opens the app after a new release ships, it pops the What's New modal for
 * them — once — then never again for that release (even across reloads).
 *
 * We wait a beat after mount so the popup doesn't fight the page's first paint,
 * and we mark "auto-shown" as soon as it opens so a mid-view refresh can't
 * re-trigger it. The badge on the bell stays lit until the user closes the
 * popup (or opens it themselves), at which point markViewed() clears it.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import { WHATS_NEW_AUTOPOPUP_ENABLED } from '@/data/whatsNew';
import { WhatsNewModal } from './WhatsNewModal';

interface WhatsNewAutoPopupProps {
  /** Office shell can deep-link to the full page; tech has no such route. */
  enableViewAll?: boolean;
}

export function WhatsNewAutoPopup({ enableViewAll = false }: WhatsNewAutoPopupProps) {
  const navigate = useNavigate();
  const { shouldAutoOpen, markAutoShown, markViewed } = useWhatsNew();
  const [isOpen, setIsOpen] = useState(false);
  const armedRef = useRef(false);

  useEffect(() => {
    // Auto-push is currently off — What's New lives behind the gift bell.
    if (!WHATS_NEW_AUTOPOPUP_ENABLED) return;
    if (!shouldAutoOpen || armedRef.current) return;
    armedRef.current = true;
    const t = setTimeout(() => {
      setIsOpen(true);
      // Stop it re-firing on reload, but keep the bell's badge lit until the
      // user actually closes the popup (markViewed does that).
      markAutoShown();
    }, 1200);
    return () => clearTimeout(t);
  }, [shouldAutoOpen, markAutoShown]);

  const handleClose = () => {
    setIsOpen(false);
    markViewed();
  };

  return (
    <WhatsNewModal
      isOpen={isOpen}
      onClose={handleClose}
      onViewAll={enableViewAll ? () => navigate('/home/whats-new') : undefined}
    />
  );
}
